// store/register-module-safe.js — idempotent Vuex module registration.
// Purpose: the app's universal Pattern-C lifecycle helper. Almost every screen in
// the deployed pro-ui calls `this.$store.registerModuleSafe("<name>", module, true)`
// on beforeCreate to attach a namespaced module scoped to that screen, and tears it
// down on leave. This helper makes registration safe to call repeatedly (route
// re-entry, HMR) without Vuex throwing "module already registered".
// Reconstructed from lacak.io deployed behavior (`registerModuleSafe`, VERBATIM
// name — SUBSYSTEMS Pattern C, HISTORY-REPORTS §1a); internal impl re-authored.

/**
 * @param {import('vuex').Store} store
 * @param {string|string[]} path        module name or nested path
 * @param {object} rawModule            a Vuex module definition
 * @param {boolean} [preserveState]     keep any state already present (SSR/HMR)
 * @returns {boolean} true if it registered, false if it was already present
 */
export function registerModuleSafe(store, path, rawModule, preserveState = false) {
  if (!store || !rawModule) return false
  // Vuex 3.6+ exposes hasModule; guard for older shims just in case.
  if (typeof store.hasModule === 'function' && store.hasModule(path)) return false
  store.registerModule(path, rawModule, { preserveState })
  return true
}

/**
 * Symmetric teardown used by screens on route leave / beforeDestroy.
 * @param {import('vuex').Store} store
 * @param {string|string[]} path
 */
export function unregisterModuleSafe(store, path) {
  if (!store) return
  if (typeof store.hasModule === 'function' && !store.hasModule(path)) return
  store.unregisterModule(path)
}

export default registerModuleSafe
